import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable, { Column } from './DataTable';
import EmptyState from './EmptyState';
import { MetricCard } from './MetricCards';
import LoadErrorBox from './ui/LoadErrorBox';
import ReadyToReceiveBanner from './procurement/ReadyToReceiveBanner';
import StatusBadge from './ui/StatusBadge';
import { idColumn } from './ui/idColumn';
import { useExport } from './header/exporters';
import { useLoad } from '../hooks/useLoad';
import { useHeaderSearch } from '../hooks/useHeaderSearch';
import { useRole } from '../hooks/useRole';
import { setShipmentsBar, useProcurementBar } from '../hooks/useProcurementBar';
import { fetchShipments, fetchShipmentsReadyToReceive, PROCUREMENT_REFRESHED_EVENT } from '../services/ProcurementService';
import { formatDecimal } from '../utils/decimal';
import { entityPath } from '../utils/entityRoutes';
import { formatDate } from '../utils/formatDate';
import { PROGRESS_VARIANT, SHIPMENT_LEG_LABELS, SHIPMENT_MODE_LABELS } from './procurement/procurementLabels';
import type { ShipmentRow } from '../types/Procurement';
import '../styles/components/ProcurementScreens.css';

/** Whether one shipment matches the header search (waybill, container, agent, order numbers, tracking numbers). */
export const shipmentMatches = (s: ShipmentRow, term: string): boolean => {
  if (!term) return true;
  return [s.waybill_no, s.container_ref, s.shipping_agent_name, s.vessel_name, s.notes, ...s.order_refs, ...s.tracking_nos]
    .some(v => (v ?? '').toLowerCase().includes(term));
};

const dateCell = (v: string | null) => (v ? formatDate(v, '') || v : '—');

/** The first few order numbers and how many more there are, so a shipment carrying dozens of orders stays one short line. */
export const previewOrderRefs = (refs: string[], limit = 4): { shown: string[]; more: number } => ({
  shown: refs.slice(0, limit),
  more: Math.max(0, refs.length - limit),
});

/** The progress statuses each summary card stands for. Arrived also covers Notified, as its count does. */
const CARD_PROGRESS: Record<string, string[]> = {
  Draft: ['Draft'],
  Booked: ['Booked'],
  'In transit': ['In transit'],
  Arrived: ['Arrived', 'Notified'],
  Received: ['Received'],
};

/** Couriers whose waybill numbers start with a fixed code (YT, JT, SF). Other numbers say nothing, so they stay blank. */
const COURIER_BY_PREFIX: Array<[RegExp, string]> = [
  [/^YT\d/i, 'YTO Express'],
  [/^JT\d/i, 'J&T Express'],
  [/^SF\d/i, 'SF Express'],
];

/** The agent to show: the one on record, else the courier the waybill number names. */
export const agentLabel = (s: Pick<ShipmentRow, 'shipping_agent_name' | 'waybill_no'>): string => {
  if (s.shipping_agent_name) return s.shipping_agent_name;
  const waybill = s.waybill_no ?? '';
  return COURIER_BY_PREFIX.find(([re]) => re.test(waybill))?.[1] ?? '—';
};

/** The date a shipment arrives or arrived: actual arrival, else the date it was received, else the estimate. */
export const etaValue = (s: Pick<ShipmentRow, 'actual_arrival' | 'received_date' | 'estimated_arrival'>): string | null =>
  s.actual_arrival ?? s.received_date ?? s.estimated_arrival ?? null;

const ShipmentsView: React.FC = () => {
  const navigate = useNavigate();
  const { canEdit, canViewFinancials } = useRole();
  const { shipments: bar } = useProcurementBar();
  const search = useHeaderSearch().trim().toLowerCase();
  const { data, loading, error, reload, setData } = useLoad(() => fetchShipments(), []);
  const [card, setCard] = useState<string | null>(null);
  const ready = useLoad(() => fetchShipmentsReadyToReceive(), []);
  const readyShipments = ready.data ?? [];

  // The breadcrumb bar's view switch shows how many are waiting; leaving the page puts the view back to the table.
  useEffect(() => { setShipmentsBar({ readyCount: readyShipments.length }); }, [readyShipments.length]);
  useEffect(() => () => setShipmentsBar({ view: 'table' }), []);

  useEffect(() => {
    const onRefreshed = (e: Event) => {
      const list = (e as CustomEvent<{ shipments?: ShipmentRow[] }>).detail?.shipments;
      if (Array.isArray(list)) setData(list);
    };
    window.addEventListener(PROCUREMENT_REFRESHED_EVENT, onRefreshed);
    return () => window.removeEventListener(PROCUREMENT_REFRESHED_EVENT, onRefreshed);
  }, [setData]);

  const rows = data ?? [];
  const shown = useMemo(
    () => rows.filter(s => (!card || (CARD_PROGRESS[card] ?? []).includes(s.progress_status)) && (!bar.status || s.status === bar.status) && (!bar.leg || s.leg === bar.leg) && (!bar.mode || s.mode === bar.mode) && shipmentMatches(s, search)),
    [rows, card, bar.status, bar.leg, bar.mode, search],
  );
  useExport('procurement-shipments', { rows: shown, ctx: undefined });

  if (error) {
    return <div className="wt-container fade-in"><LoadErrorBox what="shipments" error={error} onRetry={reload} retrying={loading} /></div>;
  }

  const readyView = bar.view === 'ready';
  const count = (progress: string) => rows.filter(s => s.progress_status === progress).length;
  const toggleCard = (name: string) => setCard(cur => (cur === name ? null : name));

  const columns: Column<ShipmentRow>[] = [
    idColumn<ShipmentRow>('shipment'),
    { key: 'waybill_no', header: 'Waybill', sortable: true, render: (_v, s) => s.waybill_no || s.container_ref || '—' },
    { key: 'shipping_agent_name', header: 'Agent', sortable: true, render: (_v, s) => agentLabel(s) },
    { key: 'mode', header: 'Mode', sortable: true, render: (_v, s) => (s.mode ? SHIPMENT_MODE_LABELS[s.mode] : '—') },
    { key: 'leg', header: 'Leg', sortable: true, render: (_v, s) => (s.leg ? SHIPMENT_LEG_LABELS[s.leg] : '—') },
    {
      key: 'progress_status', header: 'Progress', sortable: true,
      render: (_v, s) => <StatusBadge variant={PROGRESS_VARIANT[s.progress_status] ?? 'neutral'}>{s.progress_status}</StatusBadge>,
    },
    { key: 'estimated_arrival', header: 'ETA', sortable: true, render: (_v, s) => dateCell(etaValue(s)) },
    {
      key: 'order_refs', header: 'Orders',
      render: (_v, s) => {
        if (!s.order_refs.length) return '—';
        const { shown, more } = previewOrderRefs(s.order_refs);
        return <span title={s.order_refs.join(', ')}>{shown.join(', ')}{more > 0 ? ` +${more} more` : ''}</span>;
      },
    },
    { key: 'total_cbm', header: 'CBM', sortable: true, align: 'right' },
    ...(canViewFinancials ? [
      { key: 'freight_amount', header: 'Freight', align: 'right' as const, render: (_v: unknown, s: ShipmentRow) => formatDecimal(s.freight_amount, s.freight_currency) },
    ] : []),
  ];

  return (
    <div className="wt-container fade-in">
      {readyView ? (
        ready.error ? (
          <LoadErrorBox what="the shipments ready to receive" error={ready.error} onRetry={ready.reload} retrying={ready.loading} />
        ) : readyShipments.length === 0 && !ready.loading ? (
          <EmptyState icon="truck" title="Nothing is waiting to be received" description="Shipments show here once they have arrived and their goods still need confirming." />
        ) : (
          <ReadyToReceiveBanner shipments={readyShipments} mode="list" />
        )
      ) : (
      <>
      <section className="stats-grid">
        <MetricCard label="All shipments" value={rows.length} tone="pink" icon="truck" onClick={() => setCard(null)} active={card === null} title="Show all shipments" />
        <MetricCard label="Draft" value={count('Draft')} tone="blue" icon="document" onClick={() => toggleCard('Draft')} active={card === 'Draft'} title="Show draft shipments" />
        <MetricCard label="Booked" value={count('Booked')} tone="purple" icon="clock" onClick={() => toggleCard('Booked')} active={card === 'Booked'} title="Show booked shipments" />
        <MetricCard label="In transit" value={count('In transit')} tone="orange" icon="truck" onClick={() => toggleCard('In transit')} active={card === 'In transit'} title="Show shipments in transit" />
        <MetricCard label="Arrived" value={count('Arrived') + count('Notified')} tone="teal" icon="package" onClick={() => toggleCard('Arrived')} active={card === 'Arrived'} title="Show arrived shipments" />
        <MetricCard label="Received" value={count('Received')} tone="green" icon="check" onClick={() => toggleCard('Received')} active={card === 'Received'} title="Show received shipments" />
      </section>

      {ready.error ? (
        <LoadErrorBox what="the shipments ready to receive" error={ready.error} onRetry={ready.reload} retrying={ready.loading} />
      ) : (
        <ReadyToReceiveBanner shipments={readyShipments} mode="summary" onShowList={() => setShipmentsBar({ view: 'ready' })} />
      )}

      {!loading && shown.length === 0 ? (
        <EmptyState
          icon="truck"
          title={rows.length === 0 ? 'No shipments yet' : 'No shipments match these filters'}
          description={rows.length === 0 ? 'Shipments carry one or more orders from the seller to the project.' : undefined}
          action={canEdit && rows.length === 0 ? <button type="button" className="primary-btn" onClick={() => navigate('/procurement/shipments/new')}>New shipment</button> : undefined}
        />
      ) : (
        <DataTable<ShipmentRow>
          recordList={{ kind: 'shipment' }}
          data={shown}
          columns={columns}
          loading={loading}
          emptyMessage="No shipments"
          pagination={{ enabled: true, pageSize: 25 }}
          onRowClick={s => navigate(entityPath('shipment', s.id) ?? '/procurement/shipments')}
        />
      )}
      </>
      )}
    </div>
  );
};

export default ShipmentsView;
